import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";

import type { BunkerRoute } from "../lib/bunkerGeometry";
import {
  buildPopulationTransit,
  bunkerPopulationSignature,
  resolveBunkerPopulation,
  type PopulationPlacement,
  type PopulationTransit,
} from "../lib/bunkerPopulation";
import type { GameState } from "../types";
import { SurvivorFigure } from "./SurvivorFigure";

interface PopulationState {
  placements: PopulationPlacement[];
  stationaryBySpace: Map<string, PopulationPlacement[]>;
  transits: Map<string, PopulationTransit>;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => (
    typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

export function useBunkerPopulation(state: GameState, routes: BunkerRoute[]): PopulationState {
  const placements = useMemo(
    () => resolveBunkerPopulation(state, routes),
    [routes, state],
  );
  const signature = bunkerPopulationSignature(placements);
  const siteKey = `${state.site.number}:${state.site.visual_seed}`;
  const reducedMotion = usePrefersReducedMotion();
  const previousRef = useRef<Map<string, PopulationPlacement>>(new Map());
  const siteKeyRef = useRef(siteKey);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const [transits, setTransits] = useState<Map<string, PopulationTransit>>(new Map());

  useLayoutEffect(() => {
    const current = new Map(placements.map((placement) => [placement.survivor.id, placement]));
    const reset = siteKeyRef.current !== siteKey || previousRef.current.size === 0;
    if (reset || reducedMotion) {
      for (const timer of timersRef.current.values()) clearTimeout(timer);
      timersRef.current.clear();
      previousRef.current = current;
      siteKeyRef.current = siteKey;
      setTransits((existing) => existing.size ? new Map() : existing);
      return;
    }

    const nextTransits = new Map<string, PopulationTransit>();
    for (const placement of placements) {
      const previous = previousRef.current.get(placement.survivor.id);
      if (!previous || previous.transitionKey === placement.transitionKey) continue;
      const transit = buildPopulationTransit(previous, placement, routes);
      if (!transit) continue;
      nextTransits.set(placement.survivor.id, transit);
      const existingTimer = timersRef.current.get(placement.survivor.id);
      if (existingTimer) clearTimeout(existingTimer);
      timersRef.current.set(placement.survivor.id, setTimeout(() => {
        setTransits((active) => {
          if (!active.has(placement.survivor.id)) return active;
          const next = new Map(active);
          next.delete(placement.survivor.id);
          return next;
        });
        timersRef.current.delete(placement.survivor.id);
      }, transit.durationMs));
    }

    previousRef.current = current;
    siteKeyRef.current = siteKey;
    if (nextTransits.size) {
      setTransits((active) => new Map([...active, ...nextTransits]));
    }
  }, [placements, reducedMotion, routes, signature, siteKey]);

  useEffect(() => () => {
    for (const timer of timersRef.current.values()) clearTimeout(timer);
    timersRef.current.clear();
  }, []);

  const stationaryBySpace = useMemo(() => {
    const result = new Map<string, PopulationPlacement[]>();
    for (const placement of placements) {
      if (placement.zone !== "room" || transits.has(placement.survivor.id)) continue;
      result.set(placement.targetSpaceId, [
        ...(result.get(placement.targetSpaceId) ?? []),
        placement,
      ]);
    }
    return result;
  }, [placements, transits]);

  return { placements, stationaryBySpace, transits };
}

export function BunkerPopulationLayer({
  placements,
  transits,
}: {
  placements: PopulationPlacement[];
  transits: Map<string, PopulationTransit>;
}) {
  const byId = new Map(placements.map((placement) => [placement.survivor.id, placement]));
  const stationaryPassages = placements.filter((placement) => (
    placement.zone === "passage" && !transits.has(placement.survivor.id)
  ));
  const visiblePassages: PopulationPlacement[] = [];
  const passageTotals = new Map<string, number>();
  const passageAnchors = new Map<string, PopulationPlacement["anchor"]>();
  for (const placement of stationaryPassages) {
    const key = placement.passageId ?? "passage";
    const count = (passageTotals.get(key) ?? 0) + 1;
    passageTotals.set(key, count);
    passageAnchors.set(key, placement.anchor);
    if (count <= 4) visiblePassages.push(placement);
  }
  const passageOverflow = new Map<string, { count: number; anchor: PopulationPlacement["anchor"] }>();
  for (const [key, total] of passageTotals) {
    const anchor = passageAnchors.get(key);
    if (total > 4 && anchor) passageOverflow.set(key, { count: total - 4, anchor });
  }
  return (
    <div className="bunker-population-layer" data-transit-count={transits.size} aria-hidden="true">
      {visiblePassages.map((placement) => (
        <span
          className="bunker-passage-actor"
          data-passage-id={placement.passageId}
          data-survivor-id={placement.survivor.id}
          data-zone="passage"
          key={`${placement.survivor.id}:${placement.passageId}`}
          style={{
            left: `${placement.anchor.x}%`,
            top: `${placement.anchor.y}%`,
          }}
        >
          <SurvivorFigure
            activity={placement.activity}
            className="passage-survivor-figure"
            facing={placement.facing}
            lod="full"
            motion={placement.motion}
            prop={placement.prop}
            survivor={placement.survivor}
          />
        </span>
      ))}
      {[...passageOverflow.entries()].map(([passageId, overflow]) => (
        <span className="bunker-passage-overflow" key={`overflow-${passageId}`} style={{ left: `${overflow.anchor.x}%`, top: `${overflow.anchor.y}%` }}>+{overflow.count}</span>
      ))}
      {[...transits.values()].map((transit) => {
        const placement = byId.get(transit.survivorId);
        if (!placement) return null;
        const style = {
          offsetPath: `path("${transit.cssPath}")`,
          "--transit-duration": `${transit.durationMs}ms`,
        } as CSSProperties;
        return (
          <span
            className={`bunker-transit-actor transit-${transit.motion}`}
            data-from-space={transit.fromSpaceId}
            data-survivor-id={transit.survivorId}
            data-to-space={transit.toSpaceId}
            key={transit.key}
            style={style}
          >
            <SurvivorFigure
              activity={transit.motion}
              className="transit-survivor-figure"
              facing={transit.facing}
              motion={transit.motion}
              prop="none"
              survivor={placement.survivor}
            />
          </span>
        );
      })}
    </div>
  );
}
